import { observer } from 'mobx-react-lite';
import dynamic from 'next/dynamic';
import { useEffect, useMemo, useRef } from 'react';
import isObject from 'lodash/isObject';
import { DefaultSession } from 'next-auth';
import styles from '@features/navigation/components/MegaMenuDesktop/MegaMenuDesktop.styles';
import MarketingProducts from '@features/navigation/components/MarketingProducts/MarketingProducts';
import ExtraButtons from '@features/navigation/components/ExtraButtons/ExtraButtons';
import Products from '@features/navigation/components/Products/Products';
import { ECategoryFile, ICategory } from '@features/categories';
import { BehaviourType, TServiceMap } from '@features/categories/typescript';
import { useResponsive } from '@features/responsive';
import { NAVIGATION_DESKTOP_ANIMATION_WAIT_TIME } from '@features/navigation/const';
import { TImage, TVideo } from '@components/typescript';
import LoginGeneral from '@features/loginPage/components/LoginGeneral';
import { LOGIN_PAGE_TYPES } from '@features/loginPage/const';
import layoutUiState from '@features/layout/mobX/LayoutUiState';
import Icon from '@components/icons/Icon';
import Button from '@components/Button/Button';
import { getSizesString } from '@utils/misc';
import { BASE_PATH } from '@config/index';
import IsCustomerMiddleware from '@utils/IsCustomerMiddleware';
import { useTranslation } from 'next-i18next';
type PropsType = {
  onClose: () => void;
  serviceMap: TServiceMap;
  userData: DefaultSession | boolean;
  products: ICategory[];
  userPanelOpened?: boolean;
};
const DynamicUserPanelDesktop = dynamic(() => import('@features/userPanel/UserPanelDesktop'), {
  ssr: false
});
const MegaMenuDesktop = ({
  onClose,
  serviceMap,
  userData,
  products,
  userPanelOpened
}: PropsType) => {
  const {
    currentBreakpoint,
    isWiderThanDesktop
  } = useResponsive();
  const {
    t
  } = useTranslation('common');
  const {
    navigationProductItemSelected: selectedProduct,
    navigationCurrentProduct,
    setNavigationCurrentProduct,
    navigationMarketingProduct,
    setNavigationMarketingProduct,
    setLoginGeneralOpened
  } = layoutUiState;
  const selectedProductRef = useRef(selectedProduct);
  const defaultBlankImage: TVideo = {
    desktop: `${BASE_PATH}/img/no_image.svg`,
    tablet: `${BASE_PATH}/img/no_image.svg`,
    mobile: `${BASE_PATH}/img/no_image.svg`
  };
  const getCategoryMenuImage = files => {
    const subcategoryMenuImage = files.find(file => file.type === ECategoryFile.menuImage);
    if (subcategoryMenuImage) return subcategoryMenuImage;
    return false;
  };
  const handleSelectMarketingItem = (item: ICategory | null) => {
    setNavigationMarketingProduct(item);
  };
  const imageFallback = (image: TImage) => {
    if (!image) {
      return defaultBlankImage.desktop;
    }
    const {
      mobile,
      tablet,
      desktop
    } = image;
    if (desktop) return typeof desktop === 'string' ? desktop : desktop.path;
    if (tablet) return typeof tablet === 'string' ? tablet : tablet.path;
    if (mobile) return typeof mobile === 'string' ? mobile : mobile.path;
    return defaultBlankImage.desktop;
  };
  useEffect(() => {
    selectedProductRef.current = selectedProduct;
    if (!selectedProductRef.current?.children) {
      setTimeout(() => {
        if (!selectedProductRef.current) {
          setNavigationCurrentProduct(selectedProduct);
          setNavigationMarketingProduct(null);
        }
      }, NAVIGATION_DESKTOP_ANIMATION_WAIT_TIME);
      return;
    }
    setNavigationCurrentProduct(selectedProduct);
    const childrenArray: ICategory[] = Object.values(selectedProductRef.current?.children);
    if (childrenArray[0] && isWiderThanDesktop) {
      const firstCollectionBehaviour = childrenArray.find(item => item.behaviour === BehaviourType.COLLECTION);
      setNavigationMarketingProduct(firstCollectionBehaviour ?? childrenArray[0]);
    }
  }, [selectedProduct, isWiderThanDesktop]);
  const closeIconSize = useMemo(() => {
    switch (currentBreakpoint) {
      case 'ultrahd':
      case 'fullhd':
        return '26px';
      case 'widescreen':
        return '21px';
      default:
        return '16px';
    }
  }, [currentBreakpoint]);
  const isMenuVisible = useMemo(() => !(!selectedProduct && !navigationCurrentProduct) || !!userData, [navigationCurrentProduct, selectedProduct, userData]);
  const mergeRecords = (firstRecord: ICategory | null, secondRecord: ICategory | null) => {
    const firstMappedRecord: Record<number, ICategory> = {};
    const secondMappedRecord: Record<number, ICategory> = {};
    if (firstRecord && firstRecord.children) {
      Object.keys(firstRecord.children).forEach(itemId => {
        const child = firstRecord.children[itemId as unknown as symbol];
        if (child) {
          firstMappedRecord[child.id] = child;
        }
      });
    }
    if (secondRecord && secondRecord.children) {
      Object.keys(secondRecord.children).forEach(itemId => {
        const child = secondRecord.children[itemId as unknown as symbol];
        if (child) {
          secondMappedRecord[child.id] = child;
        }
      });
    }
    return {
      ...firstMappedRecord,
      ...secondMappedRecord
    };
  };
  const allImageObject = mergeRecords(navigationCurrentProduct, navigationMarketingProduct);
  const changeImage = (image: TImage, disableFade = false, productName?: string) => {
    const menuImage = document.querySelector(`.menu-marketing-product-image`) as HTMLImageElement;
    if (menuImage && image) {
      // Check for both hover and focus-visible to support keyboard navigation
      const productsItemHover = document.querySelector('.nav_menu-lvl-three:hover') as HTMLElement;
      const productsItemFocus = document.querySelector('.nav_menu-lvl-three:focus-visible') as HTMLElement;
      const productsItem = productsItemHover || productsItemFocus;
      const shouldDisableFade = disableFade || !!productsItem;
      if (!shouldDisableFade) {
        menuImage.style.transition = 'opacity 0s';
        menuImage.style.opacity = '0';
      }
      const newSrc = String(imageFallback(image) ?? '');
      const img = new Image();
      const altText = productName || navigationMarketingProduct?.name || '';
      const titleText = productName || navigationMarketingProduct?.name || '';
      const handleOpacityChange = () => {
        menuImage.style.transition = 'opacity 0s';
        menuImage.style.opacity = '1';
      };
      const handlePreloadLoad = () => {
        menuImage.src = newSrc;
        menuImage.alt = altText;
        menuImage.title = titleText;
        handleOpacityChange();
      };
      const handlePreloadError = () => {
        menuImage.src = newSrc;
        menuImage.alt = altText;
        menuImage.title = titleText;
        handleOpacityChange();
      };
      img.onload = handlePreloadLoad;
      img.onerror = handlePreloadError;
      img.src = newSrc;
      img.alt = altText;
      img.title = titleText;
      if (img.complete && img.naturalWidth !== 0) {
        setTimeout(() => {
          menuImage.src = newSrc;
          menuImage.alt = altText;
          menuImage.title = titleText;
          handleOpacityChange();
        }, 0);
      }
    }
  };
  useEffect(() => {
    const allProduct = document.querySelectorAll(`.menu-product`);
    allProduct.forEach(element => {
      (element as HTMLElement).style.display = 'none';
    });
    const menuProduct = document.querySelector(`.menu-product-${navigationMarketingProduct?.id}`) as HTMLElement;
    if (menuProduct) {
      menuProduct.style.display = 'flex';
      if (navigationMarketingProduct) {
        const allProductItems = document.querySelectorAll(`.menu-product-item`);
        allProductItems.forEach(element => {
          element.classList.remove('menu-element-active');
        });
        const productItem = document.querySelector(`.menu-product-item-${navigationMarketingProduct?.id}`) as HTMLElement;
        if (productItem) {
          productItem.classList.add('menu-element-active');
        }
        const image = getCategoryMenuImage(allImageObject[navigationMarketingProduct.id].files);
        changeImage(image);
      }
    }
  }, [navigationMarketingProduct]);
  const onHoverProduct = (file: TImage, productName?: string) => {
    changeImage(file, true, productName);
  };
  useEffect(() => {
    const allMarketingProduct = document.querySelectorAll(`.menu-marketing-product`);
    allMarketingProduct.forEach(element => {
      (element as HTMLElement).style.display = 'none';
    });
    const marketingProduct = document.querySelector(`.menu-marketing-product-${navigationCurrentProduct?.id}`) as HTMLElement;
    if (marketingProduct) {
      marketingProduct.style.display = 'flex';
    }
  }, [navigationCurrentProduct]);
  const menuAnimation = () => userData ? true : !!selectedProduct;
  useEffect(() => {
    const megaMenu = document.querySelector(`.mega-menu-container`) as HTMLElement;
    if (!userData) {
      megaMenu.style.display = 'flex';
    } else {
      megaMenu.style.display = 'none';
    }
  }, [userData]);
  const userPanel = () => <IsCustomerMiddleware>
      <div sx={styles.megaMenuUserPanelContainer(isObject(userData))} className="user-panel">
        {isObject(userData) ? <DynamicUserPanelDesktop serviceMap={serviceMap} onClosePanel={onClose} /> : <LoginGeneral showPanel viewType={LOGIN_PAGE_TYPES.general} redirectPath={false} setLoginGeneralOpened={setLoginGeneralOpened} />}
      </div>
    </IsCustomerMiddleware>;
  const menuProduct = (product: ICategory) => <Products key={`${product.slug}-products`} products={product.children} isMobile={false} onCloseMenu={onClose} serviceMap={serviceMap} onHoverProduct={onHoverProduct} topContent={<ExtraButtons product={product} onClose={onClose} serviceMap={serviceMap} />} className={`menu-product-${product.id} menu-product`} sx={{
    display: 'none'
  }} />;
  const menuContainer = useMemo(() => {
    let image = null;
    if (Object.keys(allImageObject).length !== 0) {
      image = getCategoryMenuImage(allImageObject[Object.keys(allImageObject)[0]].files);
    }
    return <div className="mega-menu-container" sx={styles.megaMenuContentContainer}>
        <div sx={styles.marketingProductsContainer} data-cy="MarketingProducts">
          {products.map(product => <div key={product.slug} className={`menu-marketing-product-${product.id} menu-marketing-product`} sx={styles.menuMarketingProduct}>
              <MarketingProducts product={product} onSelectProduct={handleSelectMarketingItem} isVisible onClose={onClose} />
            </div>)}
        </div>
        <div sx={styles.marketingCategoriesContainer}>
          {products.map(product => Object.values(product.children).map(child => menuProduct(child as ICategory)))}
          <div sx={styles.imageContainer}>
            <div sx={styles.imageWrapper}>
              <img className="menu-marketing-product-image" data-cy="MegaMenuImage" sx={styles.imageCurrent(true, true)} src={image ? String(imageFallback(image)) : `${BASE_PATH}/img/no_image.svg`} sizes={getSizesString(['50vw', '50vw', '50vw', '75vw'])} alt={navigationMarketingProduct?.listing_name || navigationMarketingProduct?.name || ''} title={navigationMarketingProduct?.listing_name || navigationMarketingProduct?.name || ''} />
            </div>
          </div>
        </div>
      </div>;
  }, [products, isMenuVisible]);
  useEffect(() => {
    if (userData && userPanelOpened) setTimeout(() => {
      window.scrollTo(0, 0);
    }, 450);
  }, [userData]);

  // Keyboard navigation in MegaMenu - WCAG 2.1.1 Keyboard, 2.4.3 Focus Order
  // Note: We only handle Arrow keys and Escape, Tab should work normally
  useEffect(() => {
    if (!isMenuVisible) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      // Only handle Arrow keys and Escape, let Tab work normally
      if (!['ArrowDown', 'ArrowUp', 'ArrowRight', 'ArrowLeft', 'Home', 'End', 'Escape'].includes(e.key)) {
        return;
      }

      // Escape closes the menu
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
        return;
      }

      // Get all focusable elements in the mega menu
      const megaMenu = document.querySelector('[data-cy="mega-menu"]') as HTMLElement;
      if (!megaMenu) return;

      // Only handle if focus is within the mega menu
      if (!megaMenu.contains(document.activeElement)) return;
      const getFocusableElements = (): HTMLElement[] => {
        const focusableSelectors = ['button:not([disabled])', 'a[href]', '[tabindex]:not([tabindex="-1"])', '[role="button"]:not([aria-disabled="true"])'].join(', ');
        const elements = Array.from(megaMenu.querySelectorAll<HTMLElement>(focusableSelectors));
        return elements.filter(el => {
          const style = window.getComputedStyle(el);
          return style.display !== 'none' && style.visibility !== 'hidden' && style.opacity !== '0' && !el.hasAttribute('disabled') && el.tabIndex !== -1 && !el.closest('[style*="display: none"]');
        });
      };
      const focusableElements = getFocusableElements();
      if (focusableElements.length === 0) return;
      const currentIndex = focusableElements.indexOf(document.activeElement as HTMLElement);
      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];
      switch (e.key) {
        case 'ArrowDown':
          {
            e.preventDefault();
            if (currentIndex < focusableElements.length - 1) {
              focusableElements[currentIndex + 1]?.focus();
            } else {
              firstElement?.focus();
            }
            break;
          }
        case 'ArrowUp':
          {
            e.preventDefault();
            if (currentIndex > 0) {
              focusableElements[currentIndex - 1]?.focus();
            } else {
              lastElement?.focus();
            }
            break;
          }
        case 'ArrowRight':
          {
            e.preventDefault();
            // Navigate to next item in same level or first item in next section
            if (currentIndex < focusableElements.length - 1) {
              focusableElements[currentIndex + 1]?.focus();
            }
            break;
          }
        case 'ArrowLeft':
          {
            e.preventDefault();
            // Navigate to previous item
            if (currentIndex > 0) {
              focusableElements[currentIndex - 1]?.focus();
            }
            break;
          }
        case 'Home':
          {
            e.preventDefault();
            firstElement?.focus();
            break;
          }
        case 'End':
          {
            e.preventDefault();
            lastElement?.focus();
            break;
          }
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isMenuVisible, onClose]);
  return <>
      <div sx={styles.megaMenuContainer(menuAnimation(), isMenuVisible)} data-cy="mega-menu">
        <div sx={styles.megaMenuContainerInner}>
          <div sx={styles.closeButtonContainer}>
            <Button variant="iconOnly" onClick={onClose} cy="closeButton" aria-label={t('close')}>
              <Icon icon="Close2" size={closeIconSize} />
            </Button>
          </div>
          {userData && userPanel()}
          {products && <div role="region" aria-label={t('productMenu') || 'Product menu'}>
              {menuContainer}
            </div>}
        </div>
      </div>
      <div sx={styles.megaMenuContainerBackdrop(menuAnimation())} />
    </>;
};
export default observer(MegaMenuDesktop);